import { createFileRoute, useNavigate, useLocation } from '@tanstack/react-router';
import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGamification } from '../hooks/useGamification';
import { usePermissions } from '../hooks/usePermissions';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import { usePermissionDeniedStore } from '../stores/permissionDenied.store';
import { useAuthStore } from '../stores/auth.store';
import { BenefitDto, BenefitRedemptionDto } from '@prometeus/contracts';
import { Breadcrumb } from '../components/Breadcrumb';
import api from '../lib/api';

// Countdown component for time-limited benefits
function CountdownTimer({ validUntil }: { validUntil: Date }) {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const end = new Date(validUntil).getTime();
      const difference = end - now;
      
      if (difference <= 0) {
        setTimeLeft('Expirado');
        return;
      }
      
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      
      if (days > 0) {
        setTimeLeft(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}m`);
      } else {
        setTimeLeft(`${minutes}m`);
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 60000);
    return () => clearInterval(timer);
  }, [validUntil]);
  
  return (
    <span 
      className="px-2 py-1 rounded-full text-xs flex items-center gap-1"
      style={{ 
        backgroundColor: '#FEF3C7', 
        color: '#92400E',
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      ⏱️ {timeLeft}
    </span>
  );
}

export const Route = createFileRoute('/w/$workspaceId/shop')({
  component: ShopPage,
});

function ShopPage() {
  const { workspaceId } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { requirePermission } = usePermissionGuard();
  const showPermissionDenied = usePermissionDeniedStore((state) => state.show);
  const user = useAuthStore((state) => state.user);
  const canRead = hasPermission('gamification.view');

  const {
    stats,
    isLoadingStats,
    benefits,
    isLoadingBenefits,
    redemptions,
    redeem,
    isRedeeming,
    } = useGamification(workspaceId, { enabled: canRead, variant: 'shop' });

  const [redeemingBenefitId, setRedeemingBenefitId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');

  // Dashboard-like metrics (for hub cards)
  const totalRedemptions = redemptions?.length || 0;
  const pendingRedemptions = redemptions?.filter((r) => r.status === 'PENDING').length || 0;
  const approvedRedemptions =
    redemptions?.filter((r) => r.status === 'APPROVED' || r.status === 'FULFILLED').length || 0;
  const progressToNextLevel = stats ? (stats.progressPercentage || 0) : 0;
  const streakMultiplier = stats?.streakMultiplier || 1;

  // Get unique categories from benefits
  const categories = useMemo(() => {
    if (!benefits) return [];
    const cats = benefits
      .map((b: BenefitDto) => b.category)
      .filter((c: string | null): c is string => c !== null && c !== '');
    return ['all', ...Array.from(new Set(cats))];
  }, [benefits]);

  // Get unique types from benefits
  const benefitTypes = useMemo(() => {
    if (!benefits) return [];
    const types = benefits.map((b: BenefitDto) => b.benefitType);
    return ['all', ...Array.from(new Set(types))];
  }, [benefits]);

  // Filter benefits
  const filteredBenefits = useMemo(() => {
    if (!benefits) return [];
    return benefits.filter((benefit: BenefitDto) => {
      const categoryMatch = selectedCategory === 'all' || benefit.category === selectedCategory;
      const typeMatch = selectedType === 'all' || benefit.benefitType === selectedType;
      return categoryMatch && typeMatch;
    });
  }, [benefits, selectedCategory, selectedType]);

  // Featured benefits
  const featuredBenefits = useMemo(() => {
    if (!benefits) return [];
    return benefits.filter((b: BenefitDto) => b.featured);
  }, [benefits]);

  // Fetch workspace for breadcrumb
  const { data: workspace } = useQuery({
    queryKey: ['workspace', workspaceId],
    queryFn: async () => {
      const response = await api.get(`/workspaces/${workspaceId}`);
      return response.data.data;
    },
    enabled: !!workspaceId && canRead,
  });

  useEffect(() => {
    if (!location.pathname.startsWith('/w/')) return;
    if (!canRead) {
      showPermissionDenied('Ver gamificação');
      navigate({ to: '/w/$workspaceId/clients', params: { workspaceId } });
    }
  }, [location.pathname, canRead, showPermissionDenied, navigate, workspaceId]);

  if (!canRead) return null;

  const handleRedeem = (benefitId: string) => {
    if (!requirePermission('gamification.benefit.redeem', 'Resgatar benefícios')) return;
    if (confirm('Tem certeza que deseja resgatar este benefício?')) {
      setRedeemingBenefitId(benefitId);
      redeem({ benefitId }, {
        onSuccess: () => {
          setRedeemingBenefitId(null);
          alert('Benefício resgatado com sucesso! Aguarde a aprovação do proprietário.');
        },
        onError: (error: any) => {
          setRedeemingBenefitId(null);
          alert(error.response?.data?.message || 'Erro ao resgatar benefício');
        },
      });
    }
  };

  const getBenefitTypeLabel = (type: string) => {
    switch (type) {
      case 'DAY_OFF': return 'Dia de Folga';
      case 'PRIZE': return 'Prêmio';
      case 'DISCOUNT': return 'Desconto';
      case 'EXPERIENCE_BOOST': return 'Boost de XP';
      case 'STREAK_SHIELD': return 'Escudo de Streak';
      case 'CUSTOM_BADGE': return 'Badge';
      case 'PRIORITY_SUPPORT': return 'Suporte VIP';
      case 'EXTRA_STORAGE': return 'Armazenamento';
      default: return 'Outro';
    }
  };

  const getBenefitTypeIcon = (type: string) => {
    switch (type) {
      case 'DAY_OFF': return '🏖️';
      case 'PRIZE': return '🎁';
      case 'DISCOUNT': return '💰';
      case 'EXPERIENCE_BOOST': return '🚀';
      case 'STREAK_SHIELD': return '🛡️';
      case 'CUSTOM_BADGE': return '🏅';
      case 'PRIORITY_SUPPORT': return '⭐';
      case 'EXTRA_STORAGE': return '💾';
      default: return '📦';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PENDING': return 'Pendente';
      case 'APPROVED': return 'Aprovado';
      case 'REJECTED': return 'Rejeitado';
      case 'FULFILLED': return 'Cumprido';
      default: return status;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      case 'APPROVED': return 'bg-blue-100 text-blue-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'FULFILLED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoadingStats || isLoadingBenefits) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <p
              className="text-center"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
              }}
            >
              Carregando...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-8 py-6">
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <p
              className="text-center"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: '12px',
                color: '#718096',
              }}
            >
              Não foi possível carregar as estatísticas.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-8 py-6">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: workspace?.name || 'Workspace', to: '/w/$workspaceId/clients', params: { workspaceId } },
            { label: 'Loja de Benefícios' },
          ]}
        />

        {/* Container no padrão do modal de aviso */}
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-[15px] shadow-xl">
          <div className="p-6 space-y-6">
            {/* Header (ícone + título + subtítulo centralizados) */}
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="rounded-full bg-amber-100 dark:bg-amber-900/30 p-3">
                  <span className="text-3xl" aria-hidden>🎁</span>
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Benefícios
              </h1>
              <p className="text-gray-600 dark:text-gray-400" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Use seus pontos para resgatar benefícios e acompanhe seus resgates.
              </p>
            </div>

            {/* Aviso destacado (mesmo padrão do modal) */}
            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
              <p className="text-sm text-yellow-800 dark:text-yellow-200" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Os resgates podem exigir aprovação do proprietário do workspace. Seus pontos são usados no momento do resgate e o status pode ficar como <strong>Pendente</strong> até a confirmação.
              </p>
            </div>

            {/* Gamification Hub (top) */}
          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              className="bg-white p-5"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#EEF2FF' }}
                >
                  <span className="text-lg">⭐</span>
                </div>
                <span
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#718096',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                >
                  Pontos
                </span>
              </div>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#4F46E5',
                }}
              >
                {stats?.points?.toLocaleString() || 0}
              </p>
            </div>

            <div
              className="bg-white p-5"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#ECFDF5' }}
                >
                  <span className="text-lg">📈</span>
                </div>
                <span
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#718096',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                >
                  Nível
                </span>
              </div>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#059669',
                }}
              >
                {stats?.level || 1}
              </p>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '11px',
                  color: '#718096',
                }}
              >
                {stats?.levelTitle || 'Iniciante'}
              </p>
            </div>

            <div
              className="bg-white p-5"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#FEF3C7' }}
                >
                  <span className="text-lg">🔥</span>
                </div>
                <span
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#718096',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                >
                  Streak
                </span>
              </div>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#B45309',
                }}
              >
                {stats?.currentStreak || 0}
                <span style={{ fontSize: '14px', fontWeight: 400, marginLeft: '4px' }}>dias</span>
              </p>
              {streakMultiplier > 1 && (
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#059669',
                  }}
                >
                  Bônus: {streakMultiplier}x pontos
                </p>
              )}
            </div>

            <div
              className="bg-white p-5"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: '#FCE7F3' }}
                >
                  <span className="text-lg">✅</span>
                </div>
                <span
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#718096',
                    textTransform: 'uppercase',
                    fontWeight: 500,
                  }}
                >
                  Tasks
                </span>
              </div>
              <p
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '24px',
                  fontWeight: 700,
                  color: '#DB2777',
                }}
              >
                {stats?.totalTasksCompleted?.toLocaleString() || 0}
              </p>
            </div>
          </div>

          {/* Progress to Next Level */}
          {stats && (
            <div className="mt-6">
              <div
                className="bg-white p-6"
                style={{
                  borderRadius: '15px',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#2D3748',
                      }}
                    >
                      Progresso para o Próximo Nível
                    </h3>
                    <p
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        color: '#718096',
                      }}
                    >
                      {stats.currentXP?.toLocaleString() || 0} / {stats.nextLevelXP?.toLocaleString() || 0} XP
                    </p>
                  </div>
                  <span
                    className="px-3 py-1 rounded-full"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      fontWeight: 600,
                      backgroundColor: '#EEF2FF',
                      color: '#4F46E5',
                    }}
                  >
                    {Math.round(progressToNextLevel)}%
                  </span>
                </div>
                <div className="h-4 rounded-full overflow-hidden" style={{ backgroundColor: '#E5E7EB' }}>
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${progressToNextLevel}%`,
                      background: 'linear-gradient(90deg, #4F46E5 0%, #7C3AED 100%)',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Streak + Redemptions */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Streak Info */}
            <div
              className="bg-white p-6"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '1rem',
                }}
              >
                🔥 Sistema de Streak
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-xl" style={{ backgroundColor: '#FEF3C7' }}>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '11px',
                      color: '#92400E',
                      marginBottom: '4px',
                    }}
                  >
                    Streak Atual
                  </p>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#B45309',
                    }}
                  >
                    {stats?.currentStreak || 0} dias
                  </p>
                </div>
                <div className="p-4 rounded-xl" style={{ backgroundColor: '#ECFDF5' }}>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '11px',
                      color: '#065F46',
                      marginBottom: '4px',
                    }}
                  >
                    Maior Streak
                  </p>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#059669',
                    }}
                  >
                    {stats?.longestStreak || 0} dias
                  </p>
                </div>
              </div>

              {/* Streak Shield Status */}
              {stats?.streakShield && (
                <div
                  className="mt-4 p-3 rounded-xl flex items-center gap-3"
                  style={{ backgroundColor: '#DBEAFE' }}
                >
                  <span className="text-xl">🛡️</span>
                  <div>
                    <p
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '12px',
                        fontWeight: 600,
                        color: '#1E40AF',
                      }}
                    >
                      Escudo de Streak Ativo
                    </p>
                    <p
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontSize: '11px',
                        color: '#1D4ED8',
                      }}
                    >
                      Seu streak está protegido por 1 dia
                    </p>
                  </div>
                </div>
              )}

              {/* Streak Multipliers Info */}
              <div className="mt-4">
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '11px',
                    color: '#718096',
                    marginBottom: '8px',
                  }}
                >
                  Multiplicadores de Streak:
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { days: '7+', mult: '1.5x', active: (stats?.currentStreak || 0) >= 7 },
                    { days: '14+', mult: '2x', active: (stats?.currentStreak || 0) >= 14 },
                    { days: '30+', mult: '2.5x', active: (stats?.currentStreak || 0) >= 30 },
                    { days: '60+', mult: '3x', active: (stats?.currentStreak || 0) >= 60 },
                  ].map((tier) => (
                    <span
                      key={tier.days}
                      className="px-2 py-1 rounded-full text-xs"
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        backgroundColor: tier.active ? '#ECFDF5' : '#F3F4F6',
                        color: tier.active ? '#059669' : '#9CA3AF',
                        border: tier.active ? '1px solid #10B981' : 'none',
                      }}
                    >
                      {tier.days}: {tier.mult} {tier.active && '✓'}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Redemptions Summary */}
            <div
              className="bg-white p-6"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <h3
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#2D3748',
                  marginBottom: '1rem',
                }}
              >
                🎁 Resumo de Resgates
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#2D3748',
                    }}
                  >
                    {totalRedemptions}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '11px',
                      color: '#718096',
                    }}
                  >
                    Total
                  </p>
                </div>
                <div className="text-center">
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#F59E0B',
                    }}
                  >
                    {pendingRedemptions}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '11px',
                      color: '#718096',
                    }}
                  >
                    Pendentes
                  </p>
                </div>
                <div className="text-center">
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '24px',
                      fontWeight: 700,
                      color: '#10B981',
                    }}
                  >
                    {approvedRedemptions}
                  </p>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '11px',
                      color: '#718096',
                    }}
                  >
                    Aprovados
                  </p>
                </div>
              </div>
              <a
                href="#my-redemptions"
                className="mt-4 block w-full text-center py-2 rounded-xl transition-colors hover:opacity-90"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: '12px',
                  backgroundColor: '#F3F4F6',
                  color: '#4B5563',
                  textDecoration: 'none',
                }}
              >
                Ver Meus Resgates →
              </a>
            </div>
          </div>
          </div>
        </div>

        {/* Featured Benefits */}
        {featuredBenefits && featuredBenefits.length > 0 && (
          <div className="mt-6">
            <div
              className="bg-gradient-to-r from-yellow-50 to-amber-50 p-6"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                border: '1px solid #FDE68A',
              }}
            >
              <div className="mb-6 flex items-center gap-2">
                <span className="text-2xl">⭐</span>
                <div>
                  <h2
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#92400E',
                      marginBottom: '0.25rem',
                    }}
                  >
                    Destaques
                  </h2>
                  <p
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#B45309',
                      fontWeight: 400,
                    }}
                  >
                    Benefícios em destaque selecionados para você
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {featuredBenefits.map((benefit: BenefitDto) => (
                  <div
                    key={benefit.id}
                    className="p-4 transition-shadow bg-white relative overflow-hidden"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      borderRadius: '15px',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.05)',
                      border: '2px solid #FDE68A',
                    }}
                  >
                    <div className="absolute top-2 right-2">
                      <span className="text-yellow-500 text-xl">★</span>
                    </div>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">{getBenefitTypeIcon(benefit.benefitType)}</span>
                      <h3
                        style={{
                          fontSize: '14px',
                          fontWeight: 600,
                          color: '#2D3748',
                        }}
                      >
                        {benefit.name}
                      </h3>
                    </div>
                    {benefit.description && (
                      <p
                        className="mb-3"
                        style={{
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {benefit.description}
                      </p>
                    )}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {benefit.validUntil && (
                        <CountdownTimer validUntil={new Date(benefit.validUntil)} />
                      )}
                      {benefit.stock !== null && (
                        <span 
                          className="px-2 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: benefit.stock <= 5 ? '#FEE2E2' : '#F0FDF4', 
                            color: benefit.stock <= 5 ? '#DC2626' : '#16A34A',
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {benefit.stock <= 5 ? '🔥' : '📦'} {benefit.stock} restantes
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <span
                          style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#B45309',
                          }}
                        >
                          {benefit.pointsCost}
                        </span>
                        <span
                          className="ml-1"
                          style={{
                            fontSize: '12px',
                            color: '#92400E',
                          }}
                        >
                          pontos
                        </span>
                      </div>
                      <button
                        onClick={() => handleRedeem(benefit.id)}
                        disabled={stats.points < benefit.pointsCost || isRedeeming || redeemingBenefitId === benefit.id || (benefit.stock !== null && benefit.stock <= 0)}
                        className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          backgroundColor: stats.points >= benefit.pointsCost && !isRedeeming && redeemingBenefitId !== benefit.id
                            ? '#B45309'
                            : '#F9FAFB',
                          color: stats.points >= benefit.pointsCost && !isRedeeming && redeemingBenefitId !== benefit.id
                            ? '#FFFFFF'
                            : '#718096',
                        }}
                      >
                        {redeemingBenefitId === benefit.id ? 'Resgatando...' : 'Resgatar'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Available Benefits */}
        <div className="mt-6">
          <div
            className="bg-white p-6"
            style={{
              borderRadius: '15px',
              boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
            }}
          >
            <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2D3748',
                    marginBottom: '0.25rem',
                  }}
                >
                  Benefícios Disponíveis
                </h2>
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                    fontWeight: 400,
                  }}
                >
                  Resgate benefícios usando seus pontos de gamificação
                </p>
              </div>
              
              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                {categories.length > 1 && (
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-[10px] text-sm"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <option value="all">Todas categorias</option>
                    {categories.filter(c => c !== 'all').map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                )}
                {benefitTypes.length > 1 && (
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-[10px] text-sm"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      fontSize: '12px',
                      color: '#2D3748',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <option value="all">Todos os tipos</option>
                    {benefitTypes.filter(t => t !== 'all').map((type) => (
                      <option key={type} value={type}>{getBenefitTypeLabel(type)}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            
            {filteredBenefits && filteredBenefits.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredBenefits.map((benefit: BenefitDto) => (
                  <div
                    key={benefit.id}
                    className="p-4 transition-shadow relative"
                    style={{
                      fontFamily: "'Poppins', sans-serif",
                      backgroundColor: '#F9FAFB',
                      borderRadius: '15px',
                      boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    {benefit.featured && (
                      <div className="absolute top-2 right-2">
                        <span className="text-yellow-500">★</span>
                      </div>
                    )}
                    <div className="flex items-start gap-3 mb-3">
                      <span className="text-2xl">{getBenefitTypeIcon(benefit.benefitType)}</span>
                      <div className="flex-1">
                        <h3
                          style={{
                            fontSize: '14px',
                            fontWeight: 600,
                            color: '#2D3748',
                          }}
                        >
                          {benefit.name}
                        </h3>
                        <span
                          className="px-2 py-0.5 rounded-full inline-block mt-1"
                          style={{
                            fontSize: '10px',
                            fontFamily: "'Poppins', sans-serif",
                            backgroundColor: '#EEF2FF',
                            color: '#4F46E5',
                          }}
                        >
                          {getBenefitTypeLabel(benefit.benefitType)}
                        </span>
                      </div>
                    </div>
                    {benefit.description && (
                      <p
                        className="mb-3"
                        style={{
                          fontSize: '12px',
                          color: '#718096',
                        }}
                      >
                        {benefit.description}
                      </p>
                    )}
                    
                    {/* Stock and Time indicators */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {benefit.validUntil && (
                        <CountdownTimer validUntil={new Date(benefit.validUntil)} />
                      )}
                      {benefit.stock !== null && (
                        <span 
                          className="px-2 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: benefit.stock <= 5 ? '#FEE2E2' : '#F0FDF4', 
                            color: benefit.stock <= 5 ? '#DC2626' : '#16A34A',
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {benefit.stock <= 5 ? '🔥' : '📦'} {benefit.stock} restantes
                        </span>
                      )}
                      {benefit.category && (
                        <span 
                          className="px-2 py-1 rounded-full text-xs"
                          style={{ 
                            backgroundColor: '#F3F4F6', 
                            color: '#6B7280',
                            fontFamily: "'Poppins', sans-serif",
                          }}
                        >
                          {benefit.category}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <span
                          style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#4F46E5',
                          }}
                        >
                          {benefit.pointsCost}
                        </span>
                        <span
                          className="ml-1"
                          style={{
                            fontSize: '12px',
                            color: '#718096',
                          }}
                        >
                          pontos
                        </span>
                      </div>
                      <button
                        onClick={() => handleRedeem(benefit.id)}
                        disabled={stats.points < benefit.pointsCost || isRedeeming || redeemingBenefitId === benefit.id || (benefit.stock !== null && benefit.stock <= 0)}
                        className="px-4 py-2 rounded-[10px] transition-colors hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          backgroundColor: stats.points >= benefit.pointsCost && !isRedeeming && redeemingBenefitId !== benefit.id && (benefit.stock === null || benefit.stock > 0)
                            ? '#03131B'
                            : '#E5E7EB',
                          color: stats.points >= benefit.pointsCost && !isRedeeming && redeemingBenefitId !== benefit.id && (benefit.stock === null || benefit.stock > 0)
                            ? '#FFFFFF'
                            : '#718096',
                        }}
                      >
                        {benefit.stock !== null && benefit.stock <= 0 
                          ? 'Esgotado' 
                          : redeemingBenefitId === benefit.id 
                            ? 'Resgatando...' 
                            : 'Resgatar'}
                      </button>
                    </div>
                    {stats.points < benefit.pointsCost && (
                      <p
                        className="mt-2"
                        style={{
                          fontSize: '11px',
                          color: '#EF4444',
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      >
                        Você precisa de mais {benefit.pointsCost - stats.points} pontos
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div
                className="p-8 text-center"
                style={{
                  backgroundColor: '#F9FAFB',
                  borderRadius: '15px',
                  boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
                }}
              >
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#718096',
                  }}
                >
                  {selectedCategory !== 'all' || selectedType !== 'all' 
                    ? 'Nenhum benefício encontrado com os filtros selecionados.'
                    : 'Nenhum benefício disponível no momento.'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* My Redemptions */}
        {redemptions && redemptions.length > 0 && (
          <div className="mt-6">
            <div
              id="my-redemptions"
              className="bg-white p-6"
              style={{
                borderRadius: '15px',
                boxShadow: '0 0 5.5px rgba(0, 0, 0, 0.02)',
              }}
            >
              <div className="mb-6">
                <h2
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#2D3748',
                    marginBottom: '0.25rem',
                  }}
                >
                  Meus Resgates
                </h2>
                <p
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: '12px',
                    color: '#A0AEC0',
                    fontWeight: 400,
                  }}
                >
                  Histórico dos seus resgates de benefícios
                </p>
              </div>
              <div className="overflow-hidden">
                <table className="w-full">
                  <thead
                    style={{
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <tr>
                      <th
                        className="px-4 py-3 text-left uppercase tracking-wider"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#718096',
                        }}
                      >
                        Benefício
                      </th>
                      <th
                        className="px-4 py-3 text-left uppercase tracking-wider"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#718096',
                        }}
                      >
                        Pontos
                      </th>
                      <th
                        className="px-4 py-3 text-left uppercase tracking-wider"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#718096',
                        }}
                      >
                        Status
                      </th>
                      <th
                        className="px-4 py-3 text-left uppercase tracking-wider"
                        style={{
                          fontFamily: "'Poppins', sans-serif",
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#718096',
                        }}
                      >
                        Data
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {redemptions.map((redemption: BenefitRedemptionDto) => (
                      <tr key={redemption.id}>
                        <td
                          className="px-4 py-3"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#2D3748',
                          }}
                        >
                          {redemption.benefit?.name || 'Benefício removido'}
                        </td>
                        <td
                          className="px-4 py-3"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#718096',
                          }}
                        >
                          {redemption.pointsSpent}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full ${getStatusColor(redemption.status)}`}
                            style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: '10px',
                              fontWeight: 500,
                            }}
                          >
                            {getStatusLabel(redemption.status)}
                          </span>
                        </td>
                        <td
                          className="px-4 py-3"
                          style={{
                            fontFamily: "'Poppins', sans-serif",
                            fontSize: '12px',
                            color: '#718096',
                          }}
                        >
                          {new Date(redemption.redeemedAt).toLocaleDateString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
